import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Bug, Lightbulb, Heart, MessageSquare, Loader2 } from "lucide-react";
import { useSubmitFeedback } from "@/hooks/use-feedback";
import type { FeedbackType } from "@/integrations/supabase/types";

const feedbackSchema = z.object({
  title: z.string().min(3, "Título deve ter no mínimo 3 caracteres").max(200),
  description: z.string().max(2000).optional(),
});

type FeedbackFormValues = z.infer<typeof feedbackSchema>;

const FEEDBACK_TYPES: { value: FeedbackType; label: string; icon: typeof Bug; color: string }[] = [
  { value: "bug_report", label: "Bug", icon: Bug, color: "text-red-500 border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-800" },
  { value: "feature_request", label: "Sugestão", icon: Lightbulb, color: "text-amber-500 border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800" },
  { value: "compliment", label: "Elogio", icon: Heart, color: "text-pink-500 border-pink-200 bg-pink-50 dark:bg-pink-950/30 dark:border-pink-800" },
  { value: "comment", label: "Comentário", icon: MessageSquare, color: "text-blue-500 border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800" },
];

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FeedbackDialog({ open, onOpenChange }: FeedbackDialogProps) {
  const [selectedType, setSelectedType] = useState<FeedbackType>("comment");
  const { mutate: submitFeedback, isPending } = useSubmitFeedback();

  const form = useForm<FeedbackFormValues>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: { title: "", description: "" },
  });

  const handleSubmit = (values: FeedbackFormValues) => {
    submitFeedback(
      {
        type: selectedType,
        title: values.title,
        description: values.description,
        page_url: window.location.pathname,
        user_agent: navigator.userAgent,
      },
      {
        onSuccess: () => {
          form.reset();
          setSelectedType("comment");
          onOpenChange(false);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Enviar Feedback</DialogTitle>
          <DialogDescription>
            Sua opinião é muito importante para nós. Conte o que está pensando!
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label>Tipo</Label>
            <div className="grid grid-cols-4 gap-2">
              {FEEDBACK_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setSelectedType(type.value)}
                  className={cn(
                    "flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all text-xs font-medium",
                    selectedType === type.value
                      ? type.color
                      : "border-border hover:border-muted-foreground/30"
                  )}
                >
                  <type.icon className="h-5 w-5" />
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="feedback-title">Título *</Label>
            <Input
              id="feedback-title"
              placeholder={
                selectedType === "bug_report" ? "Descreva o problema encontrado..." :
                selectedType === "feature_request" ? "Qual funcionalidade gostaria?" :
                selectedType === "compliment" ? "O que está gostando?" :
                "Seu comentário..."
              }
              {...form.register("title")}
            />
            {form.formState.errors.title && (
              <p className="text-xs text-destructive">{form.formState.errors.title.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="feedback-description">Detalhes (opcional)</Label>
            <Textarea
              id="feedback-description"
              placeholder="Descreva com mais detalhes..."
              rows={4}
              {...form.register("description")}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Enviar
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
